import React, { FC, useCallback } from "react";
import { connect } from "react-redux";

import { EntityType } from "@mapmycustomers/shared";
import LeaderboardMetricFieldName from "@mapmycustomers/shared/enum/fieldModel/LeaderboardMetricFieldName";
import {
  AnyEntity,
  EntityTypesSupportedColumnSelection,
} from "@mapmycustomers/shared/types/entity";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import DateRange from "@mapmycustomers/shared/types/range/DateRange";
import ViewState from "@mapmycustomers/shared/types/viewModel/ViewState";
import { getPreviewEntityUniqueElementId } from "@mapmycustomers/shared/util/browser";

import {
  DrillDownModal,
  DrillDownModalProps,
} from "@app/scene/dashboard/components/cards/common/DrillDownModal";
import { METRIC_TO_ENTITY_TYPE } from "@app/scene/dashboard/store/const";
import {
  fetchUserDrillDownData,
  hideUserDrillDown,
} from "@app/scene/dashboard/store/stackRank/actions";
import {
  getUserDrillDownTotalFilteredRecords,
  getUserDrillDownViewState,
  isUserDrillDownLoading,
} from "@app/scene/dashboard/store/stackRank/selectors";
import { DrillDownListFetcherPayload } from "@app/scene/dashboard/types/DrillDownListFetcher";
import formatDrillDownDateRange from "@app/scene/dashboard/utils/formatDrillDownDateRange";
import { showEntityView } from "@app/store/entityView/actions";
import { RootState } from "@app/store/rootReducer";

interface Props extends Pick<DrillDownModalProps, "onHide"> {
  dateRange: DateRange;
  onFetchDrillDownData: (payload: DrillDownListFetcherPayload<AnyEntity>) => void;
  onShowEntityView: typeof showEntityView;
  // onExportDrillDownData: typeof exportUserDrillDownData;
  rankingMetric: IField;
  totalFilteredRecords: number;
  viewState?: ViewState;
}

const UserDrillDown: FC<Props> = ({
  dateRange,
  onFetchDrillDownData,
  onHide,
  onShowEntityView,
  // onExportDrillDownData,
  rankingMetric,
  totalFilteredRecords,
  viewState,
}) => {
  // const handleExport = useCallback(() => {
  //   onExportDrillDownData({ configuration, scope });
  // }, [onExportDrillDownData, configuration, scope]);

  const handleActivityTypeView = useCallback(
    (entity: AnyEntity, element: HTMLElement) => {
      onShowEntityView({
        elementId: getPreviewEntityUniqueElementId(element),
        entityId: entity.id,
        entityType: entity.entity as EntityType.ACTIVITY,
      });
    },
    [onShowEntityView]
  );

  const handleRecordView = useCallback(
    (entity: AnyEntity) => {
      onShowEntityView({
        entityId: entity.id,
        entityType: entity.entity as EntityType.COMPANY | EntityType.DEAL | EntityType.PERSON,
        zIndex: 1001, // to override the drill down modal
      });
    },
    [onShowEntityView]
  );

  if (!viewState) {
    return null;
  }

  return (
    <DrillDownModal
      entityType={
        METRIC_TO_ENTITY_TYPE[
          rankingMetric.name as LeaderboardMetricFieldName
        ] as EntityTypesSupportedColumnSelection
      }
      onActivityTypeView={handleActivityTypeView}
      // onExportDrillDownData={handleExport}
      onFetchDrillDownData={onFetchDrillDownData}
      onHide={onHide}
      onRecordView={handleRecordView}
      subtitle={formatDrillDownDateRange(dateRange)}
      title={rankingMetric.displayName}
      totalFilteredRecords={totalFilteredRecords}
      viewState={viewState}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  loading: isUserDrillDownLoading(state),
  totalFilteredRecords: getUserDrillDownTotalFilteredRecords(state),
  viewState: getUserDrillDownViewState(state),
});

const mapDispatchToProps = {
  onFetchDrillDownData: fetchUserDrillDownData.request,
  onHide: hideUserDrillDown,
  // onExportDrillDownData: exportUserDrillDownData,
  onShowEntityView: showEntityView,
};

export default connect(mapStateToProps, mapDispatchToProps)(UserDrillDown);
